import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faFolderOpen, faFolderXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import Folder from "@mapmycustomers/shared/types/entity/Folder";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import User from "@mapmycustomers/shared/types/User";
import { Alert, LoadingSpinner } from "@mapmycustomers/ui";

import Pagination from "@app/component/view/ListView/components/Pagination/Pagination";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";

import ResourceType from "../../enum/ResourceType";
import {
  getFolders,
  getResources,
  getResourcesLoading,
  getResourcesViewState,
  getTotalResources,
} from "../../store";
import { fetchResources } from "../../store/actions";
import ResourcesViewState from "../../store/ResourcesViewState";
import ResourceItem from "../ResourceItem";
import ToolBar from "../ToolBar/ToolBar";

import styles from "./ResourceList.module.scss";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export interface ResourceListProps {
  currentUserId: User["id"];
  folders: Folder[];
  loading: boolean;
  onChangeResourceType: (type: ResourceType) => void;
  onFetchResources: typeof fetchResources.request;
  resources: Resource[];
  totalResources: number;
  viewState: ResourcesViewState;
}

const ResourceList: FC<ResourceListProps> = ({
  currentUserId,
  folders,
  loading,
  onChangeResourceType,
  onFetchResources,
  resources,
  totalResources,
  viewState,
}) => {
  const intl = useIntl();

  const { folderId } = useParams<{ folderId?: string }>();
  const activeFolderId = folderId ? parseInt(folderId) : undefined;

  const { range } = viewState;
  const { endRow, startRow } = range;
  const pageSize = endRow - startRow;
  const currentPage = Math.floor(range.startRow / pageSize) + 1;

  const [selectedResourceIds, setSelectedResourceIds] = useState<Resource["id"][]>([]);

  const [activeFolderName, isSharedFolder] = useMemo(() => {
    const folder = folders.find(({ id }) => id === activeFolderId);
    return [
      folder?.name ??
        intl.formatMessage({
          id: "resources.list.allResources",
          defaultMessage: "All Resources",
          description: "All resources title",
        }),
      !!folder && folder.userId !== currentUserId,
    ];
  }, [activeFolderId, currentUserId, folders, intl]);

  const isTrash = useMemo(
    () => folders.some(({ id, isTrash }) => isTrash && id === activeFolderId),
    [activeFolderId, folders]
  );

  const handlePageChange = useCallback(
    (page: number) => {
      onFetchResources({
        range: {
          endRow: pageSize * page,
          startRow: pageSize * (page - 1),
        },
      });
    },
    [onFetchResources, pageSize]
  );

  const handlePageSizeChange = useCallback(
    (pageSize: number) => {
      onFetchResources({
        range: {
          endRow: pageSize,
          startRow: 0,
        },
      });
    },
    [onFetchResources]
  );

  const handleSelectResource = useCallback((selectedId: Resource["id"], checked: boolean) => {
    if (checked) {
      setSelectedResourceIds((ids) => [...ids, selectedId]);
    } else {
      setSelectedResourceIds((ids) => ids.filter((id) => id !== selectedId));
    }
  }, []);

  useEffect(() => {
    onFetchResources({
      filter: {},
      folderId: activeFolderId,
      range: {
        endRow: pageSize,
        startRow: 0,
      },
    });
  }, [onFetchResources, activeFolderId, pageSize]);

  useEffect(() => {
    setSelectedResourceIds([]);
  }, [activeFolderId, resources, viewState]);

  return (
    <div className={styles.container}>
      <div className={styles.title}>
        <FontAwesomeIcon
          className={styles.fileIcon}
          icon={isTrash ? faFolderXmark : faFolderOpen}
        />
        <span>{activeFolderName}</span>
      </div>
      {isTrash && (
        <Alert
          message={intl.formatMessage({
            id: "resources.list.trashFolder.alert",
            defaultMessage: "Items in trash will be permanently deleted after 90 days.",
            description: "Trash folder items alert",
          })}
          showIcon
          type="info"
        />
      )}
      <ToolBar
        isSharedFolder={isSharedFolder}
        isTrash={isTrash}
        selectedResourceIds={selectedResourceIds}
      />
      {resources.length || loading ? (
        <>
          <div className={styles.list}>
            {loading ? (
              <LoadingSpinner />
            ) : (
              resources.map((resource) => (
                <ResourceItem
                  key={resource.id}
                  onSelect={handleSelectResource}
                  resource={resource}
                  selected={selectedResourceIds.includes(resource.id)}
                />
              ))
            )}
          </div>
          <Pagination
            allowedPageSizes={PAGE_SIZE_OPTIONS}
            currentPage={currentPage}
            onChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSize={pageSize}
            totalRecords={totalResources}
          />
        </>
      ) : (
        <span className={styles.noData}>
          {intl.formatMessage(
            {
              id: "resources.list.data.empty",
              defaultMessage:
                "No resources found.{allowAdd, select, true { <link>Add a link</link> or <upload>add a file</upload>.} other {}}",
              description: "No resources found info",
            },
            {
              allowAdd: !isTrash && !isSharedFolder,
              link: (text: string) => (
                <a onClick={() => onChangeResourceType(ResourceType.LINK)}>{text}</a>
              ),
              upload: (text: string) => (
                <a onClick={() => onChangeResourceType(ResourceType.FILE)}>{text}</a>
              ),
            }
          )}
        </span>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  folders: getFolders(state),
  loading: getResourcesLoading(state),
  resources: getResources(state),
  totalResources: getTotalResources(state),
  viewState: getResourcesViewState(state),
});

const mapDispatchToProps = {
  onFetchResources: fetchResources.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ResourceList);
